import type {PropertyDefinition, PropertyValue, PropertyValuesRecord} from '@github-ui/custom-properties-types'
import {areEqualValues, getEmptyValueByType, isPropertyValueArray} from '@github-ui/custom-properties-types/helpers'
import {type DependencyList, useEffect, useMemo, useState} from 'react'

import {validateValue} from '../components/validate'

export interface PropertyField {
  value?: PropertyValue
  mixed: boolean
  changed: boolean
  propertyName: string
  error?: string
}

export function useEditCustomProperties(reposProperties: PropertyValuesRecord[], deps: DependencyList = []) {
  // eslint-disable-next-line react-hooks/exhaustive-deps -- we don't expect properties to change
  const mergedValues = useMemo(() => mergeProperties(reposProperties), [])

  const [modifiedProperties, setEditingProperties] = useState<Record<string, {value: PropertyValue}>>({})
  // eslint-disable-next-line react-hooks/exhaustive-deps -- the implementation is the same as for the `useEffect` hook
  useEffect(() => setEditingProperties({}), deps)

  function setPropertyValue(property: string, value: PropertyValue) {
    setEditingProperties({...modifiedProperties, [property]: {value}})
  }

  function revertPropertyValue(property: string) {
    const updatedProperties = {...modifiedProperties}
    delete updatedProperties[property]
    setEditingProperties(updatedProperties)
  }

  const propertyValuesMap = useMemo<Record<string, PropertyField>>(() => {
    return Object.entries({...mergedValues, ...modifiedProperties}).reduce<Record<string, PropertyField>>(
      (acc, [propertyName, update]) => {
        const originalValue = mergedValues[propertyName]
        const isModified = propertyName in modifiedProperties

        const mixed = isModified ? false : !!originalValue?.mixed

        if (mixed) {
          acc[propertyName] = {propertyName, changed: false, mixed: true}
        } else {
          const changed = isModified && (originalValue?.mixed || !areEqualValues(originalValue?.value, update.value))
          const value = isModified ? update.value : originalValue?.value
          const newEntry: PropertyField = {value, changed, propertyName, mixed: false}

          const error = isModified && value && !isPropertyValueArray(value) && validateValue(value)
          if (error) {
            newEntry.error = error
          }

          acc[propertyName] = newEntry
        }

        return acc
      },
      {},
    )
  }, [modifiedProperties, mergedValues])

  return {propertyValuesMap, setPropertyValue, revertPropertyValue}
}

interface MergedValue {
  value?: PropertyValue
  mixed?: true
}

function mergeProperties(reposProperties: PropertyValuesRecord[]): Record<string, MergedValue> {
  const mergedValues: Record<string, MergedValue> = {}
  const propertiesCounter: Record<string, number> = {}
  for (const repoProperties of reposProperties) {
    for (const [propertyName, value] of Object.entries(repoProperties)) {
      const propertyValue = mergedValues[propertyName]
      if (!propertyValue) {
        mergedValues[propertyName] = {value}
      } else if (!areEqualValues(propertyValue.value, value)) {
        mergedValues[propertyName] = {mixed: true}
      }

      // Detect properties that are only present on some repos to consider them in the "mixed" state
      propertiesCounter[propertyName] ||= 0
      propertiesCounter[propertyName]++
    }
  }

  for (const [name, count] of Object.entries(propertiesCounter)) {
    const propertyValue = mergedValues[name]
    if (propertyValue && count < reposProperties.length) {
      mergedValues[name] = {mixed: true}
    }
  }

  return mergedValues
}

export function propertyFieldsToChangedPropertiesMap(
  definitions: PropertyDefinition[],
  valuesMap: Record<string, PropertyField>,
): PropertyValuesRecord {
  return definitions.reduce<PropertyValuesRecord>((acc, def) => {
    const field = valuesMap[def.propertyName]

    if (field?.changed) {
      acc[def.propertyName] = field.value || getEmptyValueByType(def.valueType)
    }

    return acc
  }, {})
}
